// src/components/AudioPlayer.tsx
import React from 'react';
import {
  Box,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  VolumeUp,
} from '@mui/icons-material';
import { useAudio } from '../hooks/useAudio';
import { useT } from '../i18n/useT';

interface AudioPlayerProps {
  audioUrl: string;
  title?: string;
  compact?: boolean;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  title = 'Audio message',
  compact = false,
}) => {
  const t = useT();
  const { isPlaying, currentAudioUrl, error, toggle } = useAudio();
  
  const isThisAudioPlaying = isPlaying && currentAudioUrl === audioUrl;

  if (compact) {
    return (
      <IconButton
        onClick={() => toggle(audioUrl)}
        color="primary"
        size="small"
        sx={{
          bgcolor: isThisAudioPlaying ? 'primary.light' : 'transparent',
          '&:hover': {
            bgcolor: isThisAudioPlaying ? 'primary.main' : 'action.hover',
          },
        }}
      >
        {isThisAudioPlaying ? <Stop /> : <VolumeUp />}
      </IconButton>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <IconButton
          onClick={() => toggle(audioUrl)}
          color="primary"
          sx={{
            bgcolor: isThisAudioPlaying ? 'primary.light' : 'action.hover',
            '&:hover': {
              bgcolor: isThisAudioPlaying ? 'primary.main' : 'action.selected',
            },
          }}
        >
          {isThisAudioPlaying ? <Stop /> : <PlayArrow />}
        </IconButton>
        
        <Box flex={1}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          {isThisAudioPlaying && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={12} />
              <Typography variant="caption" color="primary">
                
                {t("Playing...")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};